import { useMutation } from "@apollo/client";
import styled from "@emotion/styled";
import { IconInfo, IconPhoneCall } from "kompare-ui-library";
import React, { useEffect, useState } from "react";
import { callMe, i18n, useAppSelector } from "src/common";
import { Button, HtmlText, HtmlTextLink, Text } from "src/components";
import config from "src/config";
import { colors } from "src/theme";
import { Validation, validationRules } from "../forms";
import { Divider, Flex } from "../grid";
import IconModalClose from "../icons/closeModal.icon";
import { InputPhoneNumber } from "../inputs";

const CallMeModalComponent = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'rgba(50, 50, 50, 0.55)',
  transition: 'all 0.5s ease-in-out'
});

const ModalContent = styled.div({
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  borderRadius: '16px',
  maxHeight: '92%',
  width: 'calc(100% - 40px)',
  maxWidth: '340px',
  position: 'fixed',
  top: '50%',
  left: '50%',
  right: 'auto',
  bottom: 'auto',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  padding: '24px',
  overflow: 'auto',
  boxShadow: '0 4px 40px rgba(0, 0, 0, 0.06)',
  transition: 'all 0.5s ease-in-out'
});


const CloseIcon = styled(IconModalClose)({
  position: 'absolute',
  top: '8px',
  right: '8px',
});

export type CallMeModalProps = {
  open: boolean;
  onPhoneChange?: (value: string, isValid: boolean) => void;
  onSend?: () => void;
  onClose?: () => void;
  onThankYou?: () => void;
};

export const CallMeModal: React.FC<CallMeModalProps> = ({
  open,
  onPhoneChange,
  onSend,
  onClose,
  onThankYou,
}) => {
  const [formVisible, setFormVisible] = useState(true);
  const [phoneError, setPhoneError] = useState<string | undefined>(undefined);

  const initialPhoneNumber = useAppSelector(store => store.model.customer.data.phoneNumber);
  const initialCountryCode = useAppSelector(store => store.model.customer.data.countryCode);

  function getCountryCode(phoneNumber?: string) {
    if (!phoneNumber) {
      return undefined;
    }
    const match = phoneNumber.match(/^\+\d+/);
    return match ? match[0] : undefined;
  }

  const [phoneNumber, setPhoneNumber] = useState<string>(initialPhoneNumber || '');
  const [countryCode, setCountryCode] = useState<string>(initialCountryCode || getCountryCode(initialPhoneNumber) || '');

  const validations: Validation[] = [
    { type: 'required', message: 'Potreban unos' },
    { type: 'validPhoneNumber', message: 'Unesi ispravan broj mobitela', params: { regionCode: 'countryCode' } },
  ]

  const [callMeMutation] = useMutation(callMe);

  useEffect(() => {
    if (open) {
      const currentOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = currentOverflow;
      };
    }
    return () => { };
  }, [open]);

  useEffect(() => {
    setPhoneNumber(initialPhoneNumber || '');
    setCountryCode(initialCountryCode || getCountryCode(initialPhoneNumber) || '');
  }, [initialPhoneNumber, initialCountryCode]);

  if (!open) {
    return null;
  }

  const validatePhoneNumber = (phone: string, code: string) => {
    let isValid = true;
    let errorMessage;

    for (const validation of validations) {
      let params: { [key: string]: any } | undefined;

      if (validation.params) {
        params = Object.keys(validation.params).reduce((acc, key) => {
          const paramKey = validation.params![key];
          if (paramKey in { phone, code }) {
            acc[key] = (params as any)[paramKey];
          }
          return acc;
        }, {} as { [key: string]: any });
      }

      if (!validationRules[validation.type](phone, params)) {
        isValid = false;
        errorMessage = validation.message;
        break;
      }
    }

    setPhoneError(errorMessage);
    if (onPhoneChange) onPhoneChange(phone, isValid);

    return isValid;
  };

  const handleSend = () => {
    const isValid = validatePhoneNumber(phoneNumber, countryCode);
    if (!isValid) {
      return;
    }

    callMeMutation({
      variables: {
        callMeInput: { product: 'dzo', number: phoneNumber }
      }
    });

    setFormVisible(false);
    if (onSend) onSend();
  }

  const handlePhoneChange = (value: { phoneNumber: string; countryCode: string; }) => {
    setPhoneNumber(value.phoneNumber);
    setCountryCode(value.countryCode);
    const isValid = validatePhoneNumber(value.phoneNumber, value.countryCode);
    if (onPhoneChange) onPhoneChange(value.phoneNumber, isValid);
  };

  const thankYouButtonClick = () => {
    if (onThankYou) onThankYou();
    window.location.href = config.REACT_APP_KOMPARE_HOME!;
  }

  const htmlTextLinks: Array<HtmlTextLink> = [
    { value: 'form', link: 'https://kompare.hr/obrazac-za-prituzbe/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.form') },
    { value: 'mail', link: 'mailto:szop@kompare.hr', translation: 'szop@kompare.hr' },
    { value: 'here', link: 'https://kompare.hr/informacije-o-koristenju-osobnih-podataka/', translation: i18n.t('pages.customerPage.htmlTextLinkTranslations.here') },
  ];

  return (
    <CallMeModalComponent id="basicModal">
      <ModalContent>

        <CloseIcon onClick={onClose} />

        {formVisible &&
          <Flex vertical gap={24}>
            <Text type='large' bold>{i18n.t('components.modals.callMeModal.title')}</Text>
            <InputPhoneNumber label={i18n.t('components.modals.callMeModal.phone')} onChange={handlePhoneChange} value={phoneNumber} error={phoneError} />
            <Flex vertical gap={8}>
              <Button text={i18n.t('components.modals.callMeModal.send')} className="btnBlock" onClick={handleSend} />
              <Button text={i18n.t('components.modals.callMeModal.close')} btnType="textBlack" onClick={onClose} />
            </Flex>
            <Divider />
            <Flex gap={8}>
              <IconInfo width={12} height={12} color={colors.icon5} />
              <HtmlText i18nKey="components.modals.callMeModal.gdpr" links={htmlTextLinks} textType="micro" />
            </Flex>
          </Flex>
        }

        {!formVisible &&
          <Flex vertical gap={24}>
            <Flex gap={16}>
              <IconPhoneCall color={colors.primary} width={32} height={32} />
              <Text type='large' bold>{i18n.t('components.modals.callMeModal.thankYou')}</Text>
            </Flex>
            <Text type="medium">{i18n.t('components.modals.callMeModal.contactSoon')}</Text>
            <Text type="base" color={colors.text2}>{i18n.t('components.modals.callMeModal.contactSoon2')}</Text>
            <Button
              text={i18n.t('components.modals.callMeModal.returnToHome')}
              btnType="outlineBlack"
              onClick={thankYouButtonClick} />
          </Flex>
        }

      </ModalContent>
    </CallMeModalComponent>
  );
};